<template>
  <div id="sidebarNav" class="sidebarNav" :class="[showPopMenu ? 'popMenuOpen' : '', showWaiting ? 'hasWaiting' : '']">
    <nav>
      <transition-group name="basic" tag="ul">
        <li key="share" class="share">
          <screenshare-button :hide-text="false" tip-direction="above">Share</screenshare-button>
        </li>
        <li key="purpose" class="purpose" :class="[purposeShowing ? 'active' : '']">
          <button class="purposeButton" @click="handlePurpose" :disabled="purposeShowing" ref="purposeButton">
            <appIcon icon="po3" />
            <span class="text">Purpose</span>
          </button>
        </li>
        <li key="more" class="more">
          <button class="popMenuButton" @click.prevent="handlePopMenu" ref="popButton">
            <app-icon icon="menu-dots" />
            <span class="text"> More </span>
          </button>
        </li>
        <li key="waiting" v-if="showWaiting" class="waiting">
          <button class="waitingButton" @click="handleWaiting">
            <app-icon icon="menu-participants" />
            <span class="text">Waiting</span>
          </button>
        </li>
        <li key="exit" class="exit">
          <exit-meeting-button class="exitButton" :hide-text="false" tip-direction="above" name="leave" />
        </li>
      </transition-group>
    </nav>
    <transition name="popMenu">
      <div class="sidebarPopMenu" v-if="showPopMenu" v-click-outside="() => (showPopMenu = false)" @mouseover="onHovered" @mouseleave="onUnhovered">
        <ul>
          <li class="time">
            <app-button class="timeButton" @click="requestTime" :disabled="noTimerReason" @disabled-click="showPopError(noTimerReason)">
              <app-icon icon="meeting-time" />
              <span class="text">Timer</span>
            </app-button>
          </li>
          <li class="groups">
            <app-button :disabled="noGroupsReason" @disabled-click="showPopError(noGroupsReason)" @real-click="requestGroups">
              <app-icon icon="menu-groups" />
              <span class="text">Groups</span>
            </app-button>
          </li>
          <li class="breakTime">
            <app-button :disabled="noBreakReason" @disabled-click="showPopError(noBreakReason)" @real-click="requestBreak">
              <app-icon icon="menu-break" />
              <span class="text">Break</span>
            </app-button>
          </li>
          <li class="spaces">
            <app-button :disabled="noSpacesReason" @real-click="requestSpaces" @disabled-click="showPopError(noSpacesReason)">
              <app-icon icon="spaces" />
              <span class="text">Spaces</span>
            </app-button>
          </li>
          <li class="meetingNotes">
            <app-button @click="toggleNotes" :disabled="noNotesReason" @disabled-click="showPopError(noNotesReason)">
              <app-icon icon="toggle-notes" />
              <span class="text">Notes</span>
            </app-button>
          </li>
          <li class="reactions">
            <button @click="requestReactions">
              <app-icon icon="smile-control" />
              <span class="text">Reactions</span>
            </button>
          </li>
          <li class="invite">
            <button @click="showInvite = true">
              <app-icon icon="invite-plane" />
              <span class="text">Invite</span>
            </button>
          </li>
          <li class="settings">
            <button @click="requestSettings">
              <app-icon icon="menu-settings-control" />
              <span class="text">Settings</span>
            </button>
          </li>
        </ul>
        <transition name="basic">
          <div class="poster invitePanel" v-if="showInvite">
            <app-subtle-close @close="showInvite = false" />
            <invite-tip> Invite colleagues </invite-tip>
          </div>
        </transition>
        <transition name="basic">
          <div v-if="popError" class="popErrors poster" @click="popError = false">
            <app-subtle-close @close="popError = false" />
            {{ popError }}
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>
<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import { useUsersStore } from '@/store/pinia/users';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { usePhaseStore } from '@/store/pinia/phase';
import { useMeetingStore } from '@/store/pinia/meeting';
import appIcon from '@/components/AppIcon.vue';
import AppButton from '@/components/AppButton.vue';
import ExitMeetingButton from '@/components/ExitMeetingButton.vue';
import ScreenshareButton from '@/components/ScreenshareButton.vue';
import InviteTip from '@/components/InviteTip.vue';
import AppSubtleClose from '@/components/AppSubtleClose.vue';

const { $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;

const usersStore = useUsersStore();
const sidebarStore = useSidebarStore();
const phaseStore = usePhaseStore();
const meetingStore = useMeetingStore();
const { iAmModerator, inWaitingRoom, usersHere, usersStillAwayInGroup } = storeToRefs(usersStore);
const { extra: extraSidebar, current: currentSidebar } = storeToRefs(sidebarStore);
const { currentPhase, settingUp } = storeToRefs(phaseStore);
const { useNotes } = storeToRefs(meetingStore);

const popButton = ref(null);
const purposeButton = ref(null);
const showPopMenu = ref(false);
const showInvite = ref(false);
const showWaiting = computed(() => inWaitingRoom.value.length > 0 && iAmModerator.value);

let mouseOutTimeOut = null;

const noGroupsReason = computed(() => {
  if (currentPhase.value === 'lobby') {
    return 'You can only access Groups once the meeting has started';
  }
  if (usersHere.value.length < 4) {
    return 'You can only access the Groups Micro-Skill if there are four or more meeting participants.';
  }
  if (usersStillAwayInGroup.value?.length > 0) {
    return 'Some people are still away';
  }

  if (currentPhase.value !== 'freeform') {
    return 'To use Break-out Groups please end the current phase';
  }

  if (settingUp.value || currentSidebar.value.barType === 'info') {
    return 'You cannot start Break-out Groups during phase setup.';
  }

  return false;
});

const noBreakReason = computed(() => {
  if (currentPhase.value === 'lobby') {
    return 'You can only access Break once the meeting has started';
  }
  if (currentPhase.value !== 'freeform') {
    return 'To use Break please end the current phase';
  }
  if (settingUp.value || currentSidebar.value.barType === 'info') {
    return 'You cannot start a Break during phase setup.';
  }

  return false;
});

const noTimerReason = computed(() => {
  if (currentPhase.value === 'lobby') {
    return 'You can only access the Meeting Timer once the meeting has started';
  }

  return false;
});

const noNotesReason = computed(() => {
  if (currentPhase.value === 'lobby') {
    return 'You can only access the Notes once the meeting has started.';
  }

  return false;
});

const noSpacesReason = computed(() => {
  if (!iAmModerator.value) {
    return 'Meeting spaces can only be changed by the meeting owner';
  }

  return false;
});

const popError = ref(false);

const purposeShowing = computed(() => currentSidebar.value.name === 'po3' || extraSidebar.value.name === 'po3' || currentSidebar.value.name === 'lobby');

function handlePopMenu() {
  showPopMenu.value = true;
}

function handlePurpose() {
  sidebarStore.requestPane('po3', { local: true }, 'sidebarNav');
}

function handleWaiting() {
  sidebarStore.requestPane('participantList', { local: true }, 'sidebarNav');
}

function requestBreak() {
  showPopMenu.value = false;
  sidebarStore.requestPane('skillBar', { phase: 'breakTime', barType: 'info', iStarted: true, local: true }, 'popMenu');
}

function requestGroups() {
  showPopMenu.value = false;
  sidebarStore.requestPane('skillBar', { phase: 'groups', barType: 'info', iStarted: true, local: true }, 'popMenu');
}

function requestTime() {
  if (noTimerReason.value) {
    showPopError(noTimerReason.value);
    return;
  }
  showPopMenu.value = false;
  sidebarStore.requestPane('meetingTime', { local: true }, 'popMenu');
}
function requestSpaces() {
  if (noSpacesReason.value) {
    showPopError(noSpacesReason.value);
    return;
  }
  showPopMenu.value = false;
  sidebarStore.requestPane('spaces', { local: true }, 'popMenu');
}
function requestReactions() {
  showPopMenu.value = false;
  sidebarStore.requestPane('emoteGrid', { local: true }, 'popMenu');
}
function requestSettings() {
  showPopMenu.value = false;
  sidebarStore.requestPane('settings', { local: true }, 'popMenu');
}
function toggleNotes() {
  $meetingmanager.updateAdvancedSetting('notes', !useNotes.value);
}

watch(showPopMenu, (nv) => {
  if (!nv) {
    popButton.value.blur();
    showInvite.value = false;
  }
});

function showPopError(err) {
  popError.value = err;
  setTimeout(() => {
    if (popError.value === err) {
      popError.value = false;
    }
  }, 5000);
}

function onHovered() {
  clearTimeout(mouseOutTimeOut);
}

function onUnhovered() {
  mouseOutTimeOut = setTimeout(() => {
    showPopMenu.value = false;
  }, 5000);
}
</script>
<style lang="scss" scoped>
$bubble-tail-length: 18px;
$bubble-tail-width: 48px;

.sidebarNav {
  position: absolute;
  bottom: 0;
  width: rem($sidebar-width);
  right: 0;

  nav {
    position: relative;
    z-index: 2;
    ul {
      background: var(--c__bg);
      border-top: 1px solid var(--c__edges);
      border-left: 1px solid var(--c__edges);
      height: rem($log-height);
      display: flex;
      list-style: none;
      justify-content: center;
    }
    li {
      display: inline-block;
      margin: 0;
      padding: 0;
      flex: 0 0 rem(50px);
      display: flex;
      justify-content: center;
      position: relative;
    }
    li > div,
    :deep(.buttonWrapper) {
      display: contents;
    }

    :deep(button) {
      display: flex;
      flex-direction: column;
      padding: 0;
      height: auto;
      .icon {
        width: rem(40px);
        height: rem(40px);
        border-radius: 50%;
        margin-bottom: rem(2px);
      }
      .text {
        line-height: 1.25;
        margin: 0;
        @include type-size('x-small');
      }
    }
    :deep(.icon) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  :deep(.bubbleHolder) {
    top: $bubble-tail-length;
  }

  .share {
    :deep(.bubbleTip) {
      left: rem(82px);
      &::after {
        left: 22.75%;
      }
    }
    :deep(svg) {
      & > * {
        vector-effect: non-scaling-stroke;
      }
    }
  }

  .purpose {
    margin-left: rem(3px);
    .icon {
      width: rem(22px);
      :deep(svg) {
        & > * {
          vector-effect: non-scaling-stroke;
        }
      }
    }
  }

  .more {
    margin-left: rem(5px);
    .icon {
      width: rem(24px);
    }
  }
  .waitingButton {
    .icon {
      color: var(--c__bg);
      background: var(--c__orange);
    }

    :deep(svg) {
      height: rem(17px);
      width: rem(20px);

      .revFill {
        fill: var(--c__orange);
      }
    }

    @include triggered {
      .icon {
        background: var(--c__orange-darker);
      }

      :deep(.revFill) {
        fill: var(--c__orange-darker);
      }
    }
  }

  .active {
    color: var(--c__accent);
  }

  :deep(button) {
    @include triggered {
      color: var(--c__accent);
    }
    &.waitingButton {
      @include triggered {
        color: var(--c__orange-darker);
      }
    }
  }

  .exit {
    margin-left: rem(4px);
    :deep(.bubbleTip) {
      transform-origin: 77% 100% !important;
      left: rem(-81px);
      &::after {
        left: 77%;
      }
    }
  }

  &.hasWaiting {
    .exit,
    .purpose,
    .more {
      margin-left: rem(3px);
    }
    .waiting {
      margin-left: rem(2px);
    }
    .exit {
      :deep(.bubbleTip) {
        left: rem(-105px);
        transform-origin: 85% 100% !important;
        &::after {
          left: 85%;
        }
      }
    }
    .share {
      :deep(.bubbleTip) {
        left: rem(106px);
        &::after {
          left: 14.75%;
        }
      }
    }
  }
}
.sidebarPopMenu {
  background: var(--c__bg);
  position: absolute;
  bottom: rem($log-height - 1px);
  width: 100%;
  z-index: 4;

  filter: drop-shadow(0px 1px 10px var(--c__shadow));
  transform-origin: 59.25% 100% !important;
  width: rem($sidebar-width);
  transition: transform 10.5s ease;

  will-change: filter;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden; /* Chrome and Safari */
  -moz-backface-visibility: hidden; /* Firefox */

  .hasWaiting & {
    transform-origin: 50% 100% !important;
  }

  &::after {
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    left: 59.25%;
    margin-left: rem(math.div($bubble-tail-width, -2));
    border-bottom: rem($bubble-tail-length) solid transparent;
    border-top: rem($bubble-tail-length) solid var(--c__bg);
    border-left: rem(math.div($bubble-tail-width, 2)) solid transparent;
    border-right: rem(math.div($bubble-tail-width, 2)) solid transparent;
    bottom: rem(0 - ($bubble-tail-length * 2));
    margin-bottom: 1px;
    transition: all 0.3s ease;
    .hasWaiting & {
      left: 50%;
    }
  }

  ul {
    display: grid;
    grid-template-columns: repeat(4, rem(52px));
    grid-template-rows: repeat(2, rem(65px));
    grid-gap: rem(4px) rem(6px);
    list-style: none;
    margin: rem(44px) rem(40px);
  }
  li {
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s ease;
    :deep(button) {
      display: grid;
      grid-template-rows: 1fr auto;

      @include type-size('x-small');
      &:disabled {
        cursor: not-allowed;
        color: var(--c__disabled);
      }
    }
  }
  @for $i from 1 through 8 {
    li:nth-child(#{$i}) {
      transition-delay: 0.025s * ($i - 1);
    }
  }

  :deep(svg) {
    width: rem(30px);
    height: rem(30px);
    overflow: visible;
    & > * {
      vector-effect: non-scaling-stroke;
    }
  }

  &.popMenu-enter-active,
  &.popMenu-leave-active {
    transition: all $sidebar-duration $sidebar-ease-general;
  }

  &.popMenu-enter-from,
  &.popMenu-leave-to {
    transform: scale(0);
  }

  &.popMenu-enter-active,
  &.popMenu-enter-from {
    li {
      opacity: 0;
      scale: 0;
    }
  }
}
.invitePanel,
.popErrors {
  background: var(--c__bg);
  z-index: 4;
  text-align: center;
  padding: rem(34px);
  text-wrap: balance;
}

.invite {
  width: 100%;
}
</style>
